import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EVENTS, publish, subscribe, subscribeOnce } from '@sendible/event-manager';
import { routeUtils, reportErrorToFaro } from '@sendible/common';
import { ToastNotification, useToastNotificationContext } from '@sendible/design-system';
import { BridgeContextProvider, SharedStateType, getSharedState } from '@sendible/shared-state-bridge';
import { isFEV2Route } from '@sendible/frontendv2';
import { FrontendV2Pages } from '@sendible/frontendv2/src/Frontendv2Pages';
import { FrontendV2Components } from '@sendible/frontendv2/src/FrontendV2Components';
import { BackgroundUploaderProvider } from '@sendible/frontendv2/src/components/BackgroundUploader/context';
import AiAssist from '@sendible/frontendv2/src/components/AiAssist';
import { UploadsProvider } from '@sendible/frontendv2/src/models/uploads/uploadsContext';
import PageContent from './components/PageContent';
import BackboneContainer from './components/BackboneContainer';
import switchUser from './switchUser';

interface Sendible360AdapterProps {
  onTheme: (theme: string) => void;
}

const Sendible360Adapter = ({ onTheme }: Sendible360AdapterProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const aiAssistStateDefault = {
    open: false,
    quickAction: null,
    composeText: '',
  };

  /* 1. Derived State: Calculate this natively on every render instead of syncing it via useEffect */
  const isFrontendv2Route = isFEV2Route(location.pathname);

  const [aiAssistOptions, setAiAssistOptions] = useState(aiAssistStateDefault);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [hasNoWorkspaceAccess, setHasNoWorkspaceAccess] = useState(false);
  const [sharedState, setSharedState] = useState<SharedStateType>(undefined!);
  const { showToastNotification } = useToastNotificationContext();

  useEffect(() => {
    import('@sendible/sendible360').catch((error) => {
      reportErrorToFaro(error, 'CoreFacade sendible360 dynamic import');
    });

    const unsubscribeSwitchUser = subscribe(EVENTS.SWITCH_USER, switchUser);

    const unsubscribePageChange = subscribe(EVENTS.CHANGE_PAGE, (route: string) => {
      const cleanRoute = routeUtils.cleanRoute(route);
      const isNewAppRoute = isFEV2Route(route);

      if (isNewAppRoute) {
        navigate(`/${cleanRoute}`);
      } else {
        navigate(`/#${cleanRoute}`);
      }
    });
    const unsubscribeSharedState = subscribeOnce(EVENTS.SHARED_STATE_CHANGED, async () => {
      const newContext = getSharedState();

      setSharedState(newContext);
    });

    const unsubscribeAIAssistOpen = subscribe(EVENTS.SHOW_AI_ASSIST, ({ quickAction, composeText }) => {
      setAiAssistOptions({ open: true, quickAction, composeText });
    });
    const unsubscribeAIAssistClose = subscribe(EVENTS.CLOSE_AI_ASSIST, () => {
      setAiAssistOptions(aiAssistStateDefault);
    });

    const unsubscribeToastNotification = subscribe(EVENTS.TOAST_NOTIFICATION_SHOW, (notificationParams) => {
      showToastNotification(notificationParams);
    });

    return () => {
      unsubscribeSwitchUser();
      unsubscribePageChange();
      unsubscribeAIAssistOpen();
      unsubscribeAIAssistClose();
      unsubscribeSharedState();
      unsubscribeToastNotification();
    };
  }, []);

  useEffect(() => {
    if (window?.appConfig?.branding?.themeurl) onTheme(window.appConfig.branding.themeurl);
  }, [window?.appConfig?.branding?.themeurl]);

  /* 2. Optimized Effect: Manages DOM classes and side-effects without changing local state flags */
  useEffect(() => {
    const { classList } = document.body;
    const { pathname } = location;

    classList.remove('v2-app');

    if (isFrontendv2Route) {
      window.Backbone?.history.navigate('', { trigger: false });
      publish(EVENTS.ROUTE_TO_V2, pathname);
      classList.add('v2-app');
    } else {
      window.Backbone?.history.navigate(location.hash, { trigger: true });
    }
  }, [location, isFrontendv2Route]);

  useEffect(() => {
    const unsubscribe = subscribe(EVENTS.FINISH_360_BOOTSTRAP, () => {
      setIsLoading(false);
    });
    const unsubscribeNoWorkspaceAccess = subscribe(EVENTS.NO_WORKSPACE_ACCESS, () => {
      setIsLoading(false);
      setHasNoWorkspaceAccess(true);
    });

    const stopLoading = () => {
      if (isLoading) {
        setIsLoading(false);
        setHasError(true);
      }
    };
    const timeout = setTimeout(stopLoading, 45000);

    return () => {
      clearInterval(timeout);
      unsubscribe();
      unsubscribeNoWorkspaceAccess();
    };
  }, [isLoading]);

  return (
    <BackboneContainer
      shouldDisplay={!isLoading}
      hasError={hasError}
      hasNoWorkspaceAccess={hasNoWorkspaceAccess}
    >
      <BridgeContextProvider initialState={sharedState}>
        <UploadsProvider>
          <BackgroundUploaderProvider>
            <ToastNotification />
            {isFrontendv2Route && !isLoading && <FrontendV2Pages />}
            {aiAssistOptions.open && (
              <AiAssist
                quickAction={aiAssistOptions.quickAction}
                composeText={aiAssistOptions.composeText}
              />
            )}
            <FrontendV2Components />
            <PageContent isFrontendv2Route={isFrontendv2Route} />
          </BackgroundUploaderProvider>
        </UploadsProvider>
      </BridgeContextProvider>
    </BackboneContainer>
  );
};

export default Sendible360Adapter;
